import { useCallback, useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    TextField,
    FormControlLabel,
    Switch,
    Stack,
    Typography,
    Checkbox,
    Box
} from '@mui/material';
import { useAppDispatch } from '../../hooks/StoreHooks';
import { AbsenceServiceModule, CalendarServiceModule, defaultIconsByType, LeaderboardServiceModule, RankingOrder, ServiceModule, ServiceModuleTypes, TextServiceModule, TimeTrackingServiceModule, UrlServiceModule } from '../../types/serviceModuleTypes';
import {
    fetchLeaderboardActionModulesThunk,
    fetchServiceModulesThunk,
    updateCalendarModuleThunk,
    updateFileModuleThunk,
    updateFolderModuleThunk,
    updateLeaderboardModuleThunk,
    updateTextModuleThunk,
    updateTimeTrackingModuleThunk,
    updateAbsenceModuleThunk,
    updateUrlModuleThunk,
    uploadFileThunk
} from '../../store/thunk/serviceModulesThunk';
import { FormRowHalf, FormRowSwitches, StyledDialogContainer } from './Folder/FolderModule.styles';
import FileUploader from './Folder/FileUploader';
import SelectGroupsList from '../../components/groups/SelectGroupsList';
import { useTranslation } from 'react-i18next';
import { ButtonGroupRight, StickyDialogTitle, StyledDialogActions } from './ServiceModules.styles';
import { theme } from '../../theme';
import { useLeaderboardActionModules, useLeaderboardModuleById } from '../../store/selectors/serviceModulesSelector';
import TranslatableTextField from '../../components/common/translations/TranslatableTextField';
import TranslatableUrl from '../../components/common/translations/TranslatableUrl';
import { PermissionEntityType } from '../../types/permissionType';
import { usePermissionChecker } from '../../hooks/usePermissionChecker';
import IconAndColorPicker from '../../common/ui/IconAndColorPicker';
import { fileToBase64 } from '../../common/logic/fileToBase64';
import { GeneralButton } from '../../common/ui/GeneralButton';

type Props = {
    module: ServiceModule;
    onClose: () => void;
    onDelete: (id: number) => void;
};

const ServiceModuleEditor = ({ module, onClose, onDelete }: Props) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch();
    const [name, setName] = useState(module.name ?? '');
    const [assignedGroupIds, setAssignedGroupIds] = useState(module.assignedGroupIds ?? []);
    const [sendNotifications, setSendNotifications] = useState(module.sendNotification ?? false);
    const [isCompanyWideAssigned, setIsCompanyWideAssigned] = useState(module.isCompanyWideAssigned ?? true);
    const [text, setText] = useState((module as TextServiceModule).text ?? '');
    const [url, setUrl] = useState((module as UrlServiceModule).url ?? '');
    const [file, setFile] = useState<File | null>(null);
    const [urlError, setUrlError] = useState<string>('');
    const [rankingOrder, setRankingOrder] = useState<RankingOrder>(
        (module as LeaderboardServiceModule).rankingOrder ?? RankingOrder.SmallerIsBetter
    );
    const isNewModule = !module.name?.trim();
    const [isVisible, setIsVisible] = useState(
        isNewModule
            ? true
            : (module.isVisible ?? true)
    );
    const [iconData, setIconData] = useState<{
        iconName: string;
        colorHex: string;
        iconUrl: string | null;
    }>({
        iconName: module.iconName ?? (isNewModule
            ? defaultIconsByType[module.type]
            : "circle"),
        colorHex: module.colorHex ?? theme.palette.primary.main,
        iconUrl: module.iconUrl,
    });

    const { userHasPermission } = usePermissionChecker();
    const canDeleteServiceModule = userHasPermission(PermissionEntityType.ServiceModules, "Delete") ||
        (module.id != null && userHasPermission(PermissionEntityType.ServiceModules, "Delete", module.id));

    const leaderboardModule = useLeaderboardModuleById(String(module.id) || "");
    const assignedActionModuleIds = leaderboardModule?.assignedActionModuleIds || [];
    const [selectedActionModuleIds, setSelectedActionModuleIds] = useState<number[]>([]);
    const leaderboardActionModules = useLeaderboardActionModules();
    useEffect(() => {
        if (assignedActionModuleIds.length > 0) {
            setSelectedActionModuleIds(assignedActionModuleIds);
        }
    }, [assignedActionModuleIds]);

    useEffect(() => {
        if (module.type === ServiceModuleTypes.Leaderboard) {
            dispatch(fetchLeaderboardActionModulesThunk({
                isCompanyWideAssigned,
                groupIds: assignedGroupIds,
            }));
        }
    }, [isCompanyWideAssigned, assignedGroupIds.join(',')]);

    const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newUrl = e.target.value;
        setUrl(newUrl);

        if (!newUrl.trim()) {
            setUrlError(t("serviceModulesPage.folderServicePage.invalidUrl"));
        } else {
            setUrlError('');
        }
    };

    const handleCancel = () => {
        if (isNewModule) {
            onDelete(module.id);
        } else {
            onClose();
        }
    };

    const handleGroupChange = useCallback(
        (newGroupIds: number[]) => {
            setAssignedGroupIds(newGroupIds);
        },
        [dispatch, module.type]
    );

    const handleSave = async () => {
        const baseData = {
            name,
            iconName: iconData.iconName,
            colorHex: iconData.colorHex,
            isVisible,
            isCompanyWideAssigned,
            sendNotification: sendNotifications,
            assignedGroupIds: isCompanyWideAssigned ? [] : assignedGroupIds,
        };

        try {
            switch (module.type) {
                case ServiceModuleTypes.Calendar:
                    const data: Partial<CalendarServiceModule> = {
                        ...baseData,
                        anniversaryVisibility: (module as CalendarServiceModule).anniversaryVisibility,
                        birthdayVisibility: (module as CalendarServiceModule).birthdayVisibility
                    };
                    await dispatch(updateCalendarModuleThunk({ serviceModuleId: module.id, data })).unwrap();
                    break;

                case ServiceModuleTypes.Folder:
                    await dispatch(updateFolderModuleThunk({ serviceModuleId: module.id, data: baseData })).unwrap();
                    break;

                case ServiceModuleTypes.Text: {
                    const data: Partial<TextServiceModule> = { ...baseData, text };
                    await dispatch(updateTextModuleThunk({ serviceModuleId: module.id, data })).unwrap();
                    break;
                }

                case ServiceModuleTypes.Url: {
                    const data: Partial<UrlServiceModule> = { ...baseData, url };
                    await dispatch(updateUrlModuleThunk({ serviceModuleId: module.id, data })).unwrap();
                    break;
                }

                case ServiceModuleTypes.File:
                    await dispatch(updateFileModuleThunk({ serviceModuleId: module.id, data: baseData })).unwrap();
                    if (file) {
                        const base64 = await fileToBase64(file);
                        await dispatch(uploadFileThunk({ serviceModuleId: module.id, fileData: { data: base64, filename: file.name } })).unwrap();
                    }
                    break;

                case ServiceModuleTypes.Leaderboard: {
                    const data: Partial<LeaderboardServiceModule> = {
                        ...baseData,
                        assignedActionModuleIds: selectedActionModuleIds,
                        rankingOrder,
                    };
                    await dispatch(updateLeaderboardModuleThunk({ serviceModuleId: module.id, data })).unwrap();
                    break;
                }

                case ServiceModuleTypes.Timetracking: {
                    const data: Partial<TimeTrackingServiceModule> = { ...baseData };
                    await dispatch(updateTimeTrackingModuleThunk({
                        serviceModuleId: module.id,
                        data,
                    })).unwrap();
                    break;
                }

                case ServiceModuleTypes.Absence: {
                    const data: Partial<AbsenceServiceModule> = { ...baseData };
                    await dispatch(updateAbsenceModuleThunk({
                        serviceModuleId: module.id,
                        data,
                    })).unwrap();
                    break;
                }

                default:
                    console.warn("Unknown module type:", module.type);
            }
            await dispatch(fetchServiceModulesThunk())
            onClose();
        } catch (error) {
            console.error('Error saving module:', error);
        }
    };

    const renderAdditionalFields = () => {
        switch (module.type) {
            case ServiceModuleTypes.Text:
                return (
                    <TranslatableTextField
                        label={t("serviceModulesPage.folderServicePage.textContentLabel")}
                        value={text}
                        onChange={setText}
                        entityType="serviceModule"
                        entityId={String(module.id)}
                        property="text"
                        textFieldProps={{
                            multiline: true,
                            rows: 4,
                            margin: 'normal'
                        }}
                    />
                );
            case ServiceModuleTypes.Url:
                return (
                    <TranslatableUrl
                        label="URL"
                        value={url}
                        onChange={handleUrlChange}
                        urlError={urlError}
                        entityType="serviceModule"
                        entityId={String(module.id)}
                        property="url"
                    />
                );
            case ServiceModuleTypes.File:
                return (
                    <FileUploader
                        serviceModuleId={module.id}
                        file={file}
                        onFileChange={(newFile) => setFile(newFile)}
                    />
                );
            case ServiceModuleTypes.Leaderboard:
                return (
                    <Box sx={{ borderTop: '1px solid #ddd', pt: 2 }}>
                        {leaderboardActionModules.length === 0 ? (
                            <Typography>{t("serviceModulesPage.leaderboardPage.noActionModulesAvailable")}</Typography>
                        ) : (
                            <Stack spacing={1} mt={2}>
                                <Typography variant="subtitle2">
                                    {t("serviceModulesPage.leaderboardPage.selectActionModules")}
                                </Typography>
                                {leaderboardActionModules.map(({ id, name }) => (
                                    <FormControlLabel
                                        key={id}
                                        control={
                                            <Checkbox
                                                checked={selectedActionModuleIds.includes(id)}
                                                onChange={(e) => {
                                                    setSelectedActionModuleIds(prev =>
                                                        e.target.checked
                                                            ? [...prev, id]
                                                            : prev.filter(i => i !== id)
                                                    );
                                                }}
                                            />
                                        }
                                        label={name}
                                    />
                                ))}
                            </Stack>
                        )}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={rankingOrder === RankingOrder.BiggerIsBetter}
                                    onChange={(e) =>
                                        setRankingOrder(e.target.checked ? RankingOrder.BiggerIsBetter : RankingOrder.SmallerIsBetter)
                                    }
                                />
                            }
                            label={
                                rankingOrder === RankingOrder.BiggerIsBetter
                                    ? t("serviceModulesPage.leaderboardPage.biggerIsBetter")
                                    : t("serviceModulesPage.leaderboardPage.smallerIsBetter")
                            }
                        />
                    </Box>
                );
            case ServiceModuleTypes.Timetracking:
                return null;
            case ServiceModuleTypes.Absence:
                return null;
            default:
                return null;
        }
    };

    return (
        <Dialog open onClose={handleCancel} maxWidth="sm" fullWidth PaperProps={{
            sx: {
                height: '80%',
                display: 'flex',
                flexDirection: 'column',
            }
        }}>
            <StyledDialogContainer>
                <StickyDialogTitle>
                    {t("serviceModulesPage.editTitle")} ({module.type})
                </StickyDialogTitle>
                <DialogContent
                    sx={{
                        flexGrow: 1,
                        overflowY: 'auto',
                        pt: '6px !important',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Stack spacing={2}>
                        <TranslatableTextField
                            label={t("serviceModulesPage.name")}
                            value={name}
                            onChange={(val) => setName(val)}
                            entityType="serviceModule"
                            entityId={String(module.id)}
                            property="name"
                            textFieldProps={{
                                error: name.trim() === '',
                                helperText: name.trim() === '' ? t("serviceModulesPage.nameRequired") : '',
                            }}
                        />
                        <FormRowHalf>
                            <IconAndColorPicker
                                parentId={module.id}
                                parentType="serviceModule"
                                iconData={iconData}
                                uploadDisabled={!module.id}
                                onChange={(updates) => {
                                    setIconData((prev) => ({ ...prev, ...updates }));
                                }}
                            />
                        </FormRowHalf>
                        <FormRowSwitches>
                            <FormControlLabel
                                control={<Switch checked={isVisible} onChange={(e) => setIsVisible(e.target.checked)} />}
                                label={t("serviceModulesPage.isVisible")}
                            />
                            <FormControlLabel
                                control={<Switch checked={isCompanyWideAssigned} onChange={(e) => {
                                    const newValue = e.target.checked;
                                    setIsCompanyWideAssigned(newValue);
                                    if (newValue) {
                                        setAssignedGroupIds([]);
                                    }
                                }} />}
                                label={t("serviceModulesPage.companyWideAssigned")}
                            />
                        </FormRowSwitches>

                        {!isCompanyWideAssigned && (
                            <SelectGroupsList
                                value={assignedGroupIds}
                                onChange={handleGroupChange}
                            />
                        )}

                        <FormRowSwitches>
                            <FormControlLabel
                                control={<Switch checked={sendNotifications} onChange={(e) => setSendNotifications(e.target.checked)} />}
                                label={t("serviceModulesPage.sendNotifications")}
                            />
                        </FormRowSwitches>

                        {renderAdditionalFields()}
                    </Stack>
                </DialogContent>
                <StyledDialogActions>
                    {canDeleteServiceModule && (
                        <GeneralButton
                            appVariant="danger"
                            onClick={() => module.id && onDelete(module.id)}
                        >
                            {t("buttons.delete")}
                        </GeneralButton>
                    )}

                    <ButtonGroupRight>
                        <GeneralButton
                            appVariant="secondary"
                            onClick={handleCancel}
                        >
                            {t("buttons.cancel")}
                        </GeneralButton>
                        <GeneralButton
                            onClick={handleSave}
                            disabled={name.trim() === '' || (module.type === ServiceModuleTypes.Url && !!urlError)}
                        >
                            {t("buttons.save")}
                        </GeneralButton>
                    </ButtonGroupRight>
                </StyledDialogActions>
            </StyledDialogContainer>
        </Dialog>
    );
};

export default ServiceModuleEditor;
